import { useEffect, useMemo, useState } from 'react';
import { canalDeSource, CANAL_LABEL, SEDE_CODES } from '@lidotel/shared';
import { api, CANALES, type Canal, type SourceStatExtraction } from '../../lib/api.js';
import { CANAL_COLORS } from '../charts/ChartCard.js';

const MESES = [
  'enero',
  'febrero',
  'marzo',
  'abril',
  'mayo',
  'junio',
  'julio',
  'agosto',
  'septiembre',
  'octubre',
  'noviembre',
  'diciembre',
];

const empty: SourceStatExtraction = {
  sede_codigo: null,
  anio: null,
  mes: null,
  sources: [],
  grand_total_rooms: null,
};

/**
 * Revisión del reporte por Source: PDF a la izquierda, sources a la derecha con
 * el canal que le tocó a cada código.
 *
 * Los canales NO se teclean: son la suma de los códigos que el mapa reconoce, y
 * se muestran desglosados para que se vea de dónde sale cada número. Lo que sí
 * se edita son las noches de cada source, que es lo que puede leer mal el
 * modelo.
 *
 * Arriba de todo va el cuadre `Σ sources = Grand Total`: es el único control que
 * distingue «faltan noches» de «mes flojo», así que se ve antes de confirmar y
 * el backend lo bloquea si no cuadra.
 */
export function SourceReviewPanel({
  id,
  onCommitted,
  onClose,
}: {
  id: string;
  onCommitted: () => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<SourceStatExtraction>(empty);
  const [periodos, setPeriodos] = useState<{ anio: number; mes: number; noches: number }[]>([]);
  const [sobreescribir, setSobreescribir] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [issues, setIssues] = useState<string[]>([]);
  const [needsExtract, setNeedsExtract] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // El backend bloquea un descuadre contra el Grand Total. Se ofrece forzar solo
  // después de haber visto el motivo.
  const [blocked, setBlocked] = useState(false);

  function hydrate(d: SourceStatExtraction) {
    setForm({
      sede_codigo: d.sede_codigo ?? null,
      anio: d.anio ?? null,
      mes: d.mes ?? null,
      sources: d.sources ?? [],
      grand_total_rooms: d.grand_total_rooms ?? null,
    });
  }

  useEffect(() => {
    let url: string | null = null;
    api
      .ingestaGet(id)
      .then((r) => {
        if (r.extracted && 'sources' in r.extracted) hydrate(r.extracted as SourceStatExtraction);
        else setNeedsExtract(true);
        setPeriodos(r.periodosCargados ?? []);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Error'));
    api
      .ingestaFileUrl(id)
      .then((u) => {
        url = u;
        setPdfUrl(u);
      })
      .catch(() => setPdfUrl(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [id]);

  async function extract() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.ingestaExtract(id);
      if (r.tipo === 'source') hydrate(r.extracted as SourceStatExtraction);
      setNeedsExtract(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error de extracción');
    } finally {
      setBusy(false);
    }
  }

  function setNoches(fila: number, raw: string) {
    const value = raw === '' ? null : Number(raw);
    setForm((f) => ({
      ...f,
      sources: f.sources.map((s, i) => (i === fila ? { ...s, rooms: value } : s)),
    }));
  }

  async function commit(force: boolean) {
    setBusy(true);
    setError(null);
    try {
      const r = await api.ingestaCommitSource(id, { ...form, sobreescribir }, force);
      setIssues(r.issues ?? []);
      if (r.committed) onCommitted();
      else if (r.error) {
        setError(r.error);
        setBlocked(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al confirmar');
    } finally {
      setBusy(false);
    }
  }

  const porCanal = useMemo(() => {
    const acc: Record<Canal, number> = { CDR: 0, VIP: 0, VTA: 0, FRO: 0, EXP: 0, OTROS: 0 };
    for (const s of form.sources) acc[canalDeSource(s.code) as Canal] += s.rooms ?? 0;
    return acc;
  }, [form.sources]);

  const suma = form.sources.reduce((t, s) => t + (s.rooms ?? 0), 0);
  const gt = form.grand_total_rooms;
  const cuadra = gt == null || suma === gt;
  const yaCargado = periodos.find((p) => p.anio === form.anio && p.mes === form.mes);
  const periodoLabel =
    form.anio != null && form.mes != null ? `${MESES[form.mes - 1]} ${form.anio}` : 'sin período';

  return (
    <div className="rounded-xl border border-slate-700 bg-slate-800/60 p-4">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-medium text-slate-200">
          Revisión Reservation Statistics por Source
          <span className="ml-2 rounded bg-sky-900 px-1.5 py-0.5 text-[10px] text-sky-200">
            mensual · {periodoLabel}
          </span>
        </h2>
        <button onClick={onClose} className="text-sm text-slate-400 hover:text-slate-200">
          Cerrar ✕
        </button>
      </div>

      {error && (
        <p className="mb-3 rounded-lg bg-rose-950/60 px-3 py-2 text-sm text-rose-300">{error}</p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="min-h-[420px] overflow-hidden rounded-lg border border-slate-700 bg-slate-950">
          {pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="Reservation Statistics por Source"
              className="h-[420px] w-full lg:h-[640px]"
            />
          ) : (
            <p className="p-4 text-sm text-slate-500">Vista previa no disponible.</p>
          )}
        </div>

        <div>
          {needsExtract && (
            <div className="mb-4">
              <p className="mb-2 text-sm text-slate-400">Aún no se ha extraído este reporte.</p>
              <button
                onClick={extract}
                disabled={busy}
                className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {busy ? 'Extrayendo…' : 'Extraer con Gemini'}
              </button>
            </div>
          )}

          <div className="mb-3 flex flex-wrap gap-3 text-xs text-slate-400">
            <label>
              Sede
              <select
                value={form.sede_codigo ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, sede_codigo: e.target.value || null }))}
                className="mt-1 block w-32 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
              >
                <option value="">—</option>
                {SEDE_CODES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Año
              <input
                type="number"
                value={form.anio ?? ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, anio: e.target.value ? Number(e.target.value) : null }))
                }
                className="mt-1 block w-24 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
              />
            </label>
            <label>
              Mes
              <select
                value={form.mes ?? ''}
                onChange={(e) =>
                  setForm((f) => ({ ...f, mes: e.target.value ? Number(e.target.value) : null }))
                }
                className="mt-1 block w-32 rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-100"
              >
                <option value="">—</option>
                {MESES.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {form.sources.length === 0 ? (
            <p className="text-sm text-slate-500">
              Sin sources detectadas. Extrae el reporte primero.
            </p>
          ) : (
            <>
              <div className="max-h-[300px] overflow-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-slate-800">
                    <tr className="text-slate-500">
                      <th className="px-1 py-1 text-left">Source</th>
                      <th className="px-1 py-1 text-left">Canal</th>
                      <th className="px-1 py-1 text-right">Noches</th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.sources.map((s, fila) => {
                      const canal = canalDeSource(s.code) as Canal;
                      return (
                        <tr key={`${s.code}-${fila}`} className="border-t border-slate-800">
                          <td className="px-1 py-1 font-mono text-slate-300">{s.code}</td>
                          <td className="px-1 py-1">
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                aria-hidden
                                className="inline-block h-2 w-2 rounded-sm"
                                style={{ backgroundColor: CANAL_COLORS[canal] }}
                              />
                              <span
                                className={canal === 'OTROS' ? 'text-slate-500' : 'text-slate-200'}
                              >
                                {CANAL_LABEL[canal]}
                              </span>
                            </span>
                          </td>
                          <td className="px-1 py-1 text-right">
                            <input
                              type="number"
                              min="0"
                              value={s.rooms ?? ''}
                              onChange={(e) => setNoches(fila, e.target.value)}
                              className="w-20 rounded border border-slate-700 bg-slate-900 px-1 py-0.5 text-right text-slate-100"
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 rounded-lg border border-slate-700 bg-slate-900/60 p-2 text-xs">
                <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1">
                  {CANALES.map((canal) => (
                    <span key={canal} className="flex items-center gap-1.5">
                      <span
                        aria-hidden
                        className="inline-block h-2 w-2 rounded-sm"
                        style={{ backgroundColor: CANAL_COLORS[canal] }}
                      />
                      <span className="text-slate-400">{CANAL_LABEL[canal]}</span>
                      <span className="tabular-nums text-slate-100">{porCanal[canal]}</span>
                    </span>
                  ))}
                </div>
                <p className={cuadra ? 'text-slate-400' : 'font-medium text-amber-300'}>
                  Σ sources <span className="tabular-nums">{suma}</span> · Grand Total{' '}
                  <span className="tabular-nums">{gt ?? '—'}</span>{' '}
                  {cuadra ? '· cuadra ✓' : `· descuadre de ${suma - (gt ?? 0)}`}
                </p>
                {yaCargado && (
                  <label className="mt-2 flex items-center gap-2 text-amber-200">
                    <input
                      type="checkbox"
                      checked={sobreescribir}
                      onChange={(e) => setSobreescribir(e.target.checked)}
                    />
                    <span>
                      {periodoLabel} ya tiene {yaCargado.noches} noches cargadas — reemplazar el mes
                      completo
                    </span>
                  </label>
                )}
              </div>
            </>
          )}

          {issues.length > 0 && (
            <ul className="mt-3 space-y-1">
              {issues.map((it, idx) => (
                <li
                  key={idx}
                  className="rounded-md bg-amber-950/50 px-2 py-1 text-xs text-amber-200"
                >
                  {it}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => commit(false)}
              disabled={busy || needsExtract || form.sources.length === 0}
              className="rounded-md bg-emerald-600 px-3 py-1.5 text-sm text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              {busy ? 'Confirmando…' : `Confirmar ${periodoLabel}`}
            </button>
            {blocked && (
              <button
                onClick={() => commit(true)}
                disabled={busy}
                title="Guarda los valores tal cual, saltándose el cuadre contra el Grand Total"
                className="rounded-md border border-amber-600 px-3 py-1.5 text-sm text-amber-300 hover:bg-amber-950/40 disabled:opacity-50"
              >
                Confirmar de todos modos
              </button>
            )}
          </div>
          <p className="mt-2 text-xs text-slate-500">
            El canal lo resuelve el mapa de equivalencias (Caracas escribe CR, CORP VIP, CORPCCS y
            WI donde las demás sedes escriben CDR, VIP, VTA y FRO). Lo que cae en «Otros» se guarda
            igual, con su código.
          </p>
        </div>
      </div>
    </div>
  );
}
